import { Button } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { getGroupDisplayLabel, toSelector } from './helpers';
import type { useScorecardRuleStatus } from '@/hooks/useScorecardRuleStatus';
import type { ParameterGroup, ParameterTableRow } from './types';

type ScorecardRuleStatusApi = ReturnType<typeof useScorecardRuleStatus>;

export interface BuildParameterGroupColumnsOptions {
    displayBaseline: number;
    displayGroups: ParameterGroup[];
    isCanonicalValueInScorecard: ScorecardRuleStatusApi['isCanonicalValueInScorecard'];
    onOpenAddModal: (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => void;
    onQuickAddRule: (parameterRow: ParameterTableRow, label: string, group: ParameterGroup) => Promise<void>;
    parameterRow: ParameterTableRow;
    selectedScorecardIdTop: number | null;
    showAdvancedValueMetrics: boolean;
}

export const buildParameterGroupColumns = ({
    displayBaseline,
    displayGroups,
    isCanonicalValueInScorecard,
    onOpenAddModal,
    onQuickAddRule,
    parameterRow,
    selectedScorecardIdTop,
    showAdvancedValueMetrics
}: BuildParameterGroupColumnsOptions): ColumnDef<ParameterGroup>[] => {
    const groupColumns: ColumnDef<ParameterGroup>[] = [
        {
            accessorKey: 'value',
            header: 'Value',
            size: 160,
            cell: ({ row }) => {
                const { value, valueLabel } = row.original;

                const display = valueLabel ?? (typeof value === 'boolean' ? (value ? 'True' : 'False') : value?.toString() || 'null');

                return <span className="font-medium text-white">{display}</span>;
            }
        },
        {
            accessorKey: 'count',
            header: 'Count',
            size: 70,
            cell: ({ row }) => {
                if (row.original.hasData === false) {
                    return <span className="text-zinc-500 italic">0</span>;
                }

                return <span>{row.original.count}</span>;
            }
        },
        {
            accessorKey: 'avgProfit',
            header: 'Avg Profit',
            size: 100,
            cell: ({ row }) => {
                const profit = row.original.avgProfit;

                // Check if this group has no data (for options mode)
                if (row.original.hasData === false) {
                    return <span className="text-zinc-500 italic">—</span>;
                }

                return (
                    <span
                        className={`font-semibold ${
                            profit > displayBaseline ? 'text-green-400' : profit < displayBaseline ? 'text-red-400' : 'text-white'
                        }`}
                    >
                        {profit.toFixed(2)}%
                    </span>
                );
            }
        },
        {
            accessorKey: 'median',
            header: 'Median',
            size: 85,
            cell: ({ row }) => {
                const { median } = row.original;
                if (median == null) return <span className="text-zinc-500">—</span>;
                return (
                    <span
                        className={`${median > displayBaseline ? 'text-green-400' : median < displayBaseline ? 'text-red-400' : 'text-zinc-300'}`}
                    >
                        {median.toFixed(2)}%
                    </span>
                );
            }
        },
        {
            id: 'lift',
            header: 'Lift',
            size: 85,
            accessorFn: (row) => {
                // Return the lift value for sorting
                if (row.lift != null) {
                    return row.lift;
                }
                // Fallback to calculated lift

                return row.avgProfit - displayBaseline;
            },
            cell: ({ row }) => {
                const { lift } = row.original;

                if (lift != null) {
                    // Use backend-calculated lift if available
                    return (
                        <span className={`font-semibold ${lift > 0 ? 'text-green-400' : lift < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                            {lift > 0 ? '+' : ''}
                            {lift.toFixed(2)}%
                        </span>
                    );
                }

                // Fallback to client-side calculation
                const profit = row.original.avgProfit;
                const calculatedLift = profit - displayBaseline;
                return (
                    <span
                        className={`font-semibold ${
                            calculatedLift > 0 ? 'text-green-400' : calculatedLift < 0 ? 'text-red-400' : 'text-zinc-400'
                        }`}
                    >
                        {calculatedLift > 0 ? '+' : ''}
                        {calculatedLift.toFixed(2)}%
                    </span>
                );
            }
        },
        {
            id: 'ci',
            header: 'CI [95%]',
            size: 130,
            cell: ({ row }) => {
                const { ciLower, ciUpper } = row.original;
                if (ciLower == null || ciUpper == null) return <span className="text-zinc-500">—</span>;

                // Color based on whether CI includes zero (significant or not)
                const isSignificant = ciLower > 0 || ciUpper < 0;

                return (
                    <span className={`text-sm ${isSignificant ? 'text-green-400' : 'text-zinc-400'}`}>
                        [{ciLower.toFixed(1)}, {ciUpper.toFixed(1)}]%
                    </span>
                );
            }
        },
        {
            accessorKey: 'stdDev',
            header: 'Std Dev',
            size: 80,
            cell: ({ row }) => {
                const { stdDev } = row.original;
                if (stdDev == null) return <span className="text-zinc-500">—</span>;
                return <span className="text-zinc-300">±{stdDev.toFixed(2)}%</span>;
            }
        },
        {
            id: 'coverage',
            header: 'Coverage',
            size: 90,
            cell: ({ row }) => {
                const { coverage } = row.original;

                if (coverage != null) {
                    // Use backend-calculated coverage if available
                    return <span className="text-zinc-300">{(coverage * 100).toFixed(1)}%</span>;
                }

                // Fallback to client-side calculation
                const totalSamples = displayGroups.reduce((sum, g) => sum + g.count, 0);
                const calculatedCoverage = (row.original.count / totalSamples) * 100;
                return <span className="text-zinc-300">{calculatedCoverage.toFixed(1)}%</span>;
            }
        },
        {
            accessorKey: 'winRate',
            header: 'Win%',
            size: 75,
            cell: ({ row }) => {
                if (row.original.hasData === false) {
                    return <span className="text-zinc-500 italic">—</span>;
                }

                return <span className="text-white font-semibold">{row.original.winRate.toFixed(1)}%</span>;
            }
        },
        {
            accessorKey: 'beatSpyRate',
            header: 'Beat SPY%',
            size: 90,
            cell: ({ row }) => {
                const beatSpy = row.original.beatSpyRate;
                if (beatSpy == null) return <span className="text-zinc-500">—</span>;
                return <span className={`font-semibold ${beatSpy >= 50 ? 'text-green-400' : 'text-red-400'}`}>{beatSpy.toFixed(1)}%</span>;
            }
        },
        {
            accessorKey: 'avgAlpha',
            header: 'Alpha',
            size: 80,
            cell: ({ row }) => {
                const alpha = row.original.avgAlpha;
                if (alpha == null) return <span className="text-zinc-500">—</span>;
                return (
                    <span className={`font-semibold ${alpha > 0 ? 'text-green-400' : alpha < 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                        {alpha > 0 ? '+' : ''}
                        {alpha.toFixed(2)}%
                    </span>
                );
            }
        },
        {
            accessorKey: 'avgMaxProfit',
            header: 'Avg Max',
            size: 85,
            cell: ({ row }) => {
                const maxProfit = row.original.avgMaxProfit;
                if (maxProfit == null) return <span className="text-zinc-500">—</span>;
                return <span className="text-green-400">{maxProfit.toFixed(2)}%</span>;
            }
        },
        {
            accessorKey: 'avgMaxLoss',
            header: 'Avg Loss',
            size: 85,
            cell: ({ row }) => {
                const maxLoss = row.original.avgMaxLoss;
                if (maxLoss == null) return <span className="text-zinc-500">—</span>;
                return <span className="text-red-400">{maxLoss.toFixed(2)}%</span>;
            }
        },
        {
            accessorKey: 'optionAvgProfit',
            header: 'Opt Profit',
            size: 90,
            cell: ({ row }) => {
                const optProfit = row.original.optionAvgProfit;
                if (optProfit == null) return <span className="text-zinc-500">—</span>;
                return (
                    <span className={`${optProfit > 0 ? 'text-green-400' : optProfit < 0 ? 'text-red-400' : 'text-zinc-300'}`}>
                        {optProfit.toFixed(2)}%
                    </span>
                );
            }
        },
        {
            accessorKey: 'optionWinRate',
            header: 'Opt Win%',
            size: 85,
            cell: ({ row }) => {
                const optWin = row.original.optionWinRate;
                if (optWin == null) return <span className="text-zinc-500">—</span>;
                return <span className="text-white">{optWin.toFixed(1)}%</span>;
            }
        },
        {
            accessorKey: 'optionBeatStockRate',
            header: 'Opt>Stock',
            size: 90,
            cell: ({ row }) => {
                const beatStock = row.original.optionBeatStockRate;
                if (beatStock == null) return <span className="text-zinc-500">—</span>;
                return (
                    <span className={`font-semibold ${beatStock >= 50 ? 'text-green-400' : 'text-amber-400'}`}>
                        {beatStock.toFixed(1)}%
                    </span>
                );
            }
        },
        {
            id: 'range',
            header: 'Range',
            size: 120,
            cell: ({ row }) => (
                <span className="text-zinc-300 text-sm">
                    {row.original.minProfit.toFixed(1)} to {row.original.maxProfit.toFixed(1)}
                </span>
            )
        },
        {
            id: 'status',
            header: '',
            size: 40,
            cell: ({ row: groupRow }) => {
                if (!selectedScorecardIdTop) return null;

                const label = getGroupDisplayLabel(groupRow.original);

                const selector = toSelector(label, groupRow.original.value);

                const inScorecard = parameterRow.scorecardCandidate
                    ? isCanonicalValueInScorecard({
                          serializedContextFeatureRef: parameterRow.scorecardCandidate.serializedContextFeatureRef,
                          selectorType: selector.type,
                          selector
                      })
                    : false;

                return inScorecard ? (
                    <span className="text-green-400 text-lg" title="In scorecard">
                        ✓
                    </span>
                ) : null;
            }
        },
        {
            id: 'actions',
            header: 'Actions',
            size: 220,
            cell: ({ row }) => {
                const label = getGroupDisplayLabel(row.original);

                // Check if this value is already in the scorecard
                let inScorecard = false;

                if (selectedScorecardIdTop) {
                    const selector = toSelector(label, row.original.value);

                    inScorecard = parameterRow.scorecardCandidate
                        ? isCanonicalValueInScorecard({
                              serializedContextFeatureRef: parameterRow.scorecardCandidate.serializedContextFeatureRef,
                              selectorType: selector.type,
                              selector
                          })
                        : false;
                }

                return (
                    <div className="flex items-center gap-2">
                        <Button
                            disabled={!selectedScorecardIdTop || !parameterRow.scorecardCandidate || inScorecard}
                            size="sm"
                            title={
                                inScorecard
                                    ? 'Already added to scorecard'
                                    : !parameterRow.scorecardCandidate
                                      ? 'This parameter is not scorecard-authorable from the registry'
                                      : selectedScorecardIdTop
                                        ? 'Quick add to selected scorecard'
                                        : 'Select a scorecard first'
                            }
                            variant={inScorecard ? 'secondary' : 'primary'}
                            onClick={() => onQuickAddRule(parameterRow, label, row.original)}
                        >
                            {inScorecard ? '✓ Added' : 'Quick Add'}
                        </Button>
                        <Button
                            disabled={!parameterRow.scorecardCandidate}
                            size="sm"
                            title={
                                parameterRow.scorecardCandidate
                                    ? 'Add to scorecard'
                                    : 'This parameter is not scorecard-authorable from the registry'
                            }
                            variant="secondary"
                            onClick={() => onOpenAddModal(parameterRow, label, row.original)}
                        >
                            Add to Scorecard
                        </Button>
                    </div>
                );
            }
        }
    ];

    // Sticky first/last columns
    try {
        // value column sticky left
        groupColumns[0].meta = { sticky: 'left', stickyOffset: 0 };
        // actions column sticky right
        const actionsCol = groupColumns.find((c) => c.id === 'actions');
        if (actionsCol) actionsCol.meta = { sticky: 'right', stickyOffset: 0 };
    } catch {
        // no-op
    }

    // Filter columns when advanced metrics are hidden
    const columnsToUse: ColumnDef<ParameterGroup>[] = (() => {
        if (showAdvancedValueMetrics) return groupColumns;

        const essentials = new Set([
            'value',
            'count',
            'avgProfit',
            'median',
            'lift',
            'ci',
            'stdDev',
            'coverage',
            'winRate',
            'status',
            'actions'
        ]);

        return groupColumns.filter((col) => {
            const { id } = col;
            const ak = 'accessorKey' in col ? col.accessorKey : undefined;

            return essentials.has(id || ak || '');
        });
    })();

    return columnsToUse;
};
